/**
 * Tutor detail collapse.
 * Header (name + situation / folder / department badges) plus tabbed sections.
 */
import * as React from 'react';
import { User, MapPin } from 'lucide-react';
import { LabelBadge } from '@/components/common/LabelBadge';
import { Tabs, type TabItem } from '@/components/ui/tabs';
import { UserHistoryTimeline } from '@/components/common/details/UserHistoryTimeline';
import {
  TUTOR_IN_PERSON_DETAIL_TAB_CONFIGS,
  type TutorInPersonDetailTab,
} from '@/components/tutors/tutor-configs';
import { useDepartments } from '@/hooks/useDepartments';
import type { TutorRecord } from '@/types/inperson/tutor.types';
import {
  deriveTutorContentDisplay,
  deriveTutorSituation,
  getTutorDisplayName,
} from './tutor-table-utils';
import { TutorDetailsTab } from './tutor-detail-tabs/TutorDetailsTab';
import { TutorRegistrationTab } from './tutor-detail-tabs/TutorRegistrationTab';
import { TutorInfoSessionTab } from './tutor-detail-tabs/TutorInfoSessionTab';
import { TutorAssignmentTab } from './tutor-detail-tabs/TutorAssignmentTab';
import { TutorDocumentsTab } from './tutor-detail-tabs/TutorDocumentsTab';

/** Props for {@link TutorDetails}. */
interface Props {
  record: TutorRecord;
}

/** Tutor detail collapse. */
export const TutorDetails: React.FC<Props> = ({ record }) => {
  const [activeTab, setActiveTab] = React.useState<TutorInPersonDetailTab>('details');
  const { data: departments = [] } = useDepartments('FR');
  /** Flattened labels used by every detail tab. */
  const display = deriveTutorContentDisplay(record, departments);
  /** Situation chip config + icon for the header badge. */
  const { situationConfig, SituationIcon } = deriveTutorSituation(record);

  /** Tab items from {@link TUTOR_IN_PERSON_DETAIL_TAB_CONFIGS}. */
  const tabs: TabItem<TutorInPersonDetailTab>[] = TUTOR_IN_PERSON_DETAIL_TAB_CONFIGS.map(
    ({ id, label, icon: Icon }) => ({
      id,
      label,
      icon: <Icon className="w-3 h-3" />,
    }),
  );

  return (
    <>
      <div className="details-view-header">
        <div className="details-view-header-left">
          <div className="details-view-header-icon">
            <User className="w-6 h-6" style={{ color: 'var(--color-2-nuance-1)' }} />
          </div>
          <div className="details-view-header-content">
            <div className="details-view-header-with-badges">
              <h3 className="details-view-header-title">{getTutorDisplayName(record)}</h3>
              <LabelBadge
                label={situationConfig.label}
                icon={SituationIcon}
                variant={situationConfig.badgeVariant}
                color={situationConfig.badgeColor}
                size="sm"
              />
              <LabelBadge
                label={display.folderConfig.label}
                icon={display.folderConfig.icon}
                variant={display.folderConfig.badgeVariant}
                color={display.folderConfig.badgeColor}
                size="sm"
              />
              {display.departments.length > 0 && (
                <LabelBadge
                  label={display.departments.join(', ')}
                  icon={MapPin}
                  variant="outlined"
                  color="var(--color-1-nuance-1)"
                  size="sm"
                />
              )}
            </div>
          </div>
        </div>
      </div>

      <Tabs items={tabs} activeTab={activeTab} onTabChange={(id) => setActiveTab(id)} />

      <div className="details-view-grid">
        {activeTab === 'details' && <TutorDetailsTab record={record} display={display} />}
        {activeTab === 'registration' && <TutorRegistrationTab display={display} />}
        {activeTab === 'documents' && <TutorDocumentsTab display={display} />}
        {activeTab === 'info_session' && <TutorInfoSessionTab display={display} />}
        {activeTab === 'assignment' && (
          <TutorAssignmentTab display={display} situationConfig={situationConfig} />
        )}
        {activeTab === 'history' && (
          <UserHistoryTimeline userId={display.userId} role="tutor_in_person" />
        )}
      </div>
    </>
  );
};

/** Factory for the {@link DataTable} expanded-row collapse. */
export const renderTutorDetails = (record: TutorRecord): React.ReactNode => {
  return <TutorDetails record={record} />;
};
